import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import ReportTemplate from '../../components/reportPage/ReportTemplate';
import { fromDealerJob } from '../../components/reportPage/adapters/dealer';
import { jobLoader, reportFileUrl, useDealerJob } from '../../components/reportPage/useDealerJob';
import { useReportService } from '../../services/reportService';
import { isReportUnavailable } from '../../components/reportPage/reportAccess';
import { useOwnReport } from '../../components/reportPage/useOwnReport';
import { routesPath } from '../../common/constants/routesPath';
import UpgradeBanner from '../../components/upgrade/UpgradeBanner';
import { existingReportPath } from '../../common/constants/reportPlans';
import { showsUpgradeBanner } from './upgradeBannerVisibility';
import sampleReportResponse from './sampleReportData.json';
import {
    translationNotice,
    translationStatus,
    useRetry,
    withTranslationNotice,
} from '../../components/reportPage/translationNotice';

const SAMPLE_JOB = {
    ...sampleReportResponse,
    reportState: 'ready',
    report: { ...sampleReportResponse.report, vinCode: sampleReportResponse.vin },
};

/**
 * Standard report (1 credit): the dealer's service documents, read by the parser. Until a parsed report exists
 * the page shows what stands in for it: waiting for the dealer, the original PDF, or the dealer's answer.
 */
const Report = ({ sample, shared }) => {
    const { t, i18n } = useTranslation('reportPage');
    const lang = i18n.resolvedLanguage;
    const [attempt, retry] = useRetry();
    const params = useParams();
    const locationState = useLocation().state;
    const jobUuid = params.id || (locationState && locationState.record && locationState.record.uuid);
    const { getReport, getReportBySharedLink } = useReportService();

    const load = jobLoader({
        sample: sample && SAMPLE_JOB,
        sharedLink: shared && params.sharedLink,
        jobUuid,
        getReport,
        getReportBySharedLink,
        lang,
    });
    const { job, loading, error } = useDealerJob(load, [sample, shared, params.sharedLink, jobUuid, lang], attempt);
    // not the user's order: offer their own report for the VIN, which the dashboard passes along; a pasted link has
    // none, so it is asked for by the order's uuid (SC-QA-77)
    const denied = !sample && !shared && isReportUnavailable(error);
    const own = useOwnReport(locationState && locationState.record && locationState.record.vin, denied, jobUuid);

    const report = useMemo(
        () =>
            job
                ? withTranslationNotice(
                      fromDealerJob(job, t, {
                          originalUrl: sample ? '' : reportFileUrl(job),
                          supportHref: routesPath.PUBLIC.SUPPORT,
                      }),
                      translationNotice(translationStatus(job.translation), t, retry)
                  )
                : null,
        [job, t, sample, retry]
    );

    // SC-QA-55: the owner's order upgraded to Complete opens as Complete; only a shared link keeps the Standard view
    if (!sample && !shared && job && job.type === 'combined' && job.vin) {
        return <Navigate to={existingReportPath('complete', job.vin, jobUuid)} replace />;
    }

    return (
        <ReportTemplate
            report={report}
            loading={loading || own.loading}
            unavailable={denied ? { ownRecord: own.record } : null}
            banner={
                // SC-QA-27: the user's own Standard report offers the upgrade to Complete
                job && showsUpgradeBanner({ sample, shared, sharedLinkParam: params.sharedLink, vin: job.vin }) ? (
                    <UpgradeBanner vin={job.vin} jobUuid={jobUuid} />
                ) : null
            }
            showBack={!shared && !sample}
            sharedLink={!shared && !sample && job && job.report ? job.report.sharedLink : null}
            printTitle={job ? `${String(job.vin || '').toUpperCase()} - CheckVin.ai` : undefined}
        />
    );
};

export default Report;
